/** Use to wrap {x, y, z} objects and do non-mutating vector math on them. */
export class vec3 {
    static xyz(v) {
        return { x: v.x, y: v.y, z: v.z };
    }
    static tuple(v) {
        return [v.x, v.y, v.z];
    }
    static addScalar(a, b) {
        return { x: a.x + b, y: a.y + b, z: a.z + b };
    }
    static add(a, b) {
        return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
    }
    static subScalar(a, b) {
        return { x: a.x - b, y: a.y - b, z: a.z - b };
    }
    /**
     * Subtract b from a.
     */
    static sub(a, b) {
        return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
    }
    static negate(v) {
        return { x: -v.x, y: -v.y, z: -v.z };
    }
    static normalize(v) {
        const len = vec3.len(v);
        return len === 0 ? undefined : { x: v.x / len, y: v.y / len, z: v.z / len };
    }
    static scale(v, s) {
        s = typeof s === "number" ? { x: s, y: s, z: s } : s;
        return { x: v.x * s.x, y: v.y * s.y, z: v.z * s.z };
    }
    static midpoint(a, b) {
        return { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5, z: (a.z + b.z) * 0.5 };
    }
    /**
     * Sets the length of input vector by specified length. Positive length values are expected. Negative length values
     * are clamped to zero.
     */
    static scaleToLength(v, length) {
        const vLength = vec3.len(v);
        if (vLength === 0) {
            return {
                x: 0,
                y: 0,
                z: 0,
            };
        }
        const scale = Math.max(0, length) / vLength;
        return { x: v.x * scale, y: v.y * scale, z: v.z * scale };
    }
    static distance(a, b) {
        return Math.sqrt(vec3.distanceSq(a, b));
    }
    static distanceSq(a, b) {
        return (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
    }
    static len(v) {
        return Math.sqrt(v.x ** 2 + v.y ** 2 + v.z ** 2);
    }
    static dot(a, b) {
        return a.x * b.x + a.y * b.y + a.z * b.z;
    }
    static eq(a, b) {
        return a.x === b.x && a.y === b.y && a.z === b.z;
    }
    static isZero(a) {
        return a.x === 0 && a.y === 0 && a.z === 0;
    }
    static round(v, p) {
        const precision = Math.pow(10, p);
        return {
            x: Math.round(v.x * precision) / precision,
            y: Math.round(v.y * precision) / precision,
            z: Math.round(v.z * precision) / precision,
        };
    }
    static toVec2(v) {
        return { x: v.x, y: v.y };
    }
    static applyMatrix4(v, m) {
        const x = v.x, y = v.y, z = v.z;
        const e = m;
        const w = 1 / (e[3] * x + e[7] * y + e[11] * z + e[15]);
        const nx = (e[0] * x + e[4] * y + e[8] * z + e[12]) * w;
        const ny = (e[1] * x + e[5] * y + e[9] * z + e[13]) * w;
        const nz = (e[2] * x + e[6] * y + e[10] * z + e[14]) * w;
        return { x: nx, y: ny, z: nz };
    }
    static chain(vec) {
        const v = Array.isArray(vec) ? { x: vec[0], y: vec[1], z: vec[2] } : vec;
        return {
            add: (v2) => vec3.chain(vec3.add(v, v2)),
            sub: (v2) => vec3.chain(vec3.sub(v, v2)),
            negate: () => vec3.chain(vec3.negate(v)),
            scale: (s) => vec3.chain(vec3.scale(v, s)),
            len: () => vec3.len(v),
            midpoint: (v2) => vec3.midpoint(v, v2),
            dot: (v2) => vec3.dot(v, v2),
            distance: (v2) => vec3.distance(v, v2),
            normalize: () => {
                const n = vec3.normalize(v);
                return n ? vec3.chain(n) : undefined;
            },
            eq: (v2) => vec3.eq(v, v2),
            round: (p) => vec3.chain(vec3.round(v, p)),
            xyz: () => vec3.xyz(v),
            tuple: () => vec3.tuple(v),
            isZero: () => vec3.isZero(v),
            toVec2: () => vec3.toVec2(v),
        };
    }
}
//# sourceMappingURL=vec3.js.map